import { FC } from 'react';
import { Box, Typography, Avatar, Stack } from '@mui/material';
import { Tooltip } from 'framework/ui/mui-builder';
import { PersonItem } from '../../types';
import EmployeePersonTile from '../../../PersonTile/EmployeePersonTile';

interface PeopleSectionProps {
  people: PersonItem[];
  maxItems?: number;
  showPosition?: boolean;
  title?: string;
  isOpenInNewTab?: boolean;
}

const PeopleSection: FC<PeopleSectionProps> = ({
  people,
  maxItems = 4,
  showPosition = true,
  title,
  isOpenInNewTab,
}) => {
  if (!people || !people.length) {
    return (
      <Box>
        {title && (
          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            {title} (0)
          </Typography>
        )}

        <Typography variant="body1" color="textSecondary" fontStyle="italic">
          No team members available
        </Typography>
      </Box>
    );
  }

  const displayedPeople = people.slice(0, maxItems);
  const hasMore = people.length > maxItems;
  const remainingCount = people.length - maxItems;

  const tooltipContent = (
    <Box>
      {people.map(person => (
        <Box mb={1} key={`tooltip-${person.id}-${person.role || 'no-role'}`} display="flex" alignItems="center">
          <Avatar src={person.avatar} alt={person.name} sx={{ width: 24, height: 24, mr: 1, fontSize: '12px' }}>
            {person.name.charAt(0)}
          </Avatar>

          <Box>
            <Typography variant="subtitle1">{person.name}</Typography>
            {showPosition && person.position && (
              <Typography variant="body2" color="textSecondary">
                {person.position}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const content = (
    <Box>
      {title && (
        <Typography variant="subtitle2" color="textSecondary" mb={1}>
          {title} ({people.length})
        </Typography>
      )}

      <Stack spacing={1}>
        {displayedPeople.map(person => {
          const isIdOnly = person.name === person.id;
          const uniqueKey = `person-${person.id}-${person.role || 'no-role'}`;

          if (isIdOnly) {
            return (
              <EmployeePersonTile
                key={uniqueKey}
                employeeId={person.id as string}
                role={person.role}
                size="small"
                showPosition={showPosition}
                isLink
                isOpenInNewTab={isOpenInNewTab}
              />
            );
          }

          return (
            <EmployeePersonTile
              key={uniqueKey}
              person={{
                id: person.id,
                name: person.name,
                position: person.position,
                department: person.department,
                email: person.email,
                employeePhotoId: person.avatar,
                avatar: person.avatar,
              }}
              size="small"
              showPosition={showPosition}
              isOpenInNewTab={isOpenInNewTab}
            />
          );
        })}

        {hasMore && (
          <Typography variant="body2" color="textSecondary" pl={1}>
            ...and {remainingCount} more
          </Typography>
        )}
      </Stack>
    </Box>
  );

  return hasMore ? (
    <Tooltip title={tooltipContent} arrow placement="top">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default PeopleSection;
